import React, { useState, useEffect } from 'react';
import { Badge, List, Modal, Button, Typography, Space, Empty } from 'antd';
import { MessageOutlined, CheckOutlined, ClockCircleOutlined, BellFilled } from '@ant-design/icons';
import { database, ref, onValue, update } from '../../pages/Auth/db/firebase';

const { Text, Paragraph } = Typography;

const AdminNotificationCenter = ({ facilityId, uid }) => {
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [netflixModalOpen, setNetflixModalOpen] = useState(false);
  const [latestUnreadNotification, setLatestUnreadNotification] = useState(null);

  useEffect(() => {
    if (!facilityId || !uid) return;

    const notificationsRef = ref(database, `notifications/user_messages/${facilityId}/${uid}`);
    const unsubscribe = onValue(notificationsRef, (snapshot) => {
      const data = snapshot.val();
      if (!data) {
        setNotifications([]);
        setUnreadCount(0);
        return;
      }

      const notificationsList = Object.entries(data).map(([id, notification]) => ({
        id,
        ...notification,
        timestamp: new Date(notification.createdAt || Date.now()).getTime()
      })).sort((a, b) => b.timestamp - a.timestamp);

      const unread = notificationsList.filter(n => !n.read);

      setNotifications(notificationsList);
      setUnreadCount(unread.length);

      // Show Netflix-style modal for new unread messages
      const newestUnread = unread[0];
      if (newestUnread && (!latestUnreadNotification || newestUnread.timestamp > latestUnreadNotification.timestamp)) {
        setLatestUnreadNotification(newestUnread);
        setNetflixModalOpen(true);
      }
    });

    return () => unsubscribe();
  }, [facilityId, uid, latestUnreadNotification]);

  const markAsRead = async (notificationId) => {
    if (!facilityId || !uid) return;

    try {
      await update(ref(database, `notifications/user_messages/${facilityId}/${uid}/${notificationId}`), {
        read: true,
        readAt: new Date().toISOString()
      });
    } catch (error) {
      console.error('Failed to mark notification as read:', error);
    }
  };

  const markAllAsRead = async () => {
    if (!facilityId || !uid) return;

    try {
      const updates = {};
      notifications.filter(n => !n.read).forEach(notification => {
        updates[`notifications/user_messages/${facilityId}/${uid}/${notification.id}/read`] = true;
        updates[`notifications/user_messages/${facilityId}/${uid}/${notification.id}/readAt`] = new Date().toISOString();
      });

      await update(ref(database), updates);
    } catch (error) {
      console.error('Failed to mark all notifications as read:', error);
    }
  };

  const formatTimeAgo = (timestamp) => {
    const now = Date.now();
    const diff = now - timestamp;
    const minutes = Math.floor(diff / 60000);
    const hours = Math.floor(diff / 3600000);
    const days = Math.floor(diff / 86400000);

    if (days > 0) return `${days}d ago`;
    if (hours > 0) return `${hours}h ago`;
    if (minutes > 0) return `${minutes}m ago`;
    return 'Just now';
  };

  const handleNetflixModalClose = () => {
    setNetflixModalOpen(false);
    if (latestUnreadNotification) {
      markAsRead(latestUnreadNotification.id);
    }
  };

  return (
    <>
      {/* Notification Center Button */}
      <Button
        type="text"
        icon={
          <Badge count={unreadCount} size="small" offset={[10, 0]}>
            <BellFilled style={{ fontSize: '18px', color: '#1890ff' }} />
          </Badge>
        }
        onClick={() => setIsModalOpen(true)}
        style={{ border: 'none', boxShadow: 'none' }}
      />

      {/* Notification Center Modal */}
      <Modal
        title={
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span>Notification Center</span>
            {unreadCount > 0 && (
              <Button size="small" type="link" onClick={markAllAsRead}>
                Mark all as read
              </Button>
            )}
          </div>
        }
        open={isModalOpen}
        onCancel={() => setIsModalOpen(false)}
        footer={null}
        width={600}
        style={{ top: 50 }}
      >
        {notifications.length === 0 ? (
          <Empty
            description="No notifications yet"
            image={Empty.PRESENTED_IMAGE_SIMPLE}
          />
        ) : (
          <List
            dataSource={notifications}
            renderItem={(notification) => (
              <List.Item
                style={{
                  backgroundColor: notification.read ? 'transparent' : 'rgba(24, 144, 255, 0.1)',
                  borderRadius: '8px',
                  marginBottom: '8px',
                  padding: '12px'
                }}
                actions={[
                  !notification.read && (
                    <Button
                      type="link"
                      size="small"
                      icon={<CheckOutlined />}
                      onClick={() => markAsRead(notification.id)}
                    >
                      Mark as read
                    </Button>
                  )
                ]}
              >
                <List.Item.Meta
                  avatar={
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <MessageOutlined style={{ color: '#1890ff', fontSize: '16px' }} />
                      {!notification.read && (
                        <Badge status="processing" />
                      )}
                    </div>
                  }
                  title={
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Text strong={!notification.read}>{notification.title}</Text>
                      <Space>
                        <ClockCircleOutlined style={{ fontSize: '12px', color: '#999' }} />
                        <Text type="secondary" style={{ fontSize: '12px' }}>
                          {formatTimeAgo(notification.timestamp)}
                        </Text>
                      </Space>
                    </div>
                  }
                  description={
                    <Paragraph
                      style={{
                        marginBottom: 0,
                        color: notification.read ? '#999' : 'inherit'
                      }}
                    >
                      {notification.message}
                    </Paragraph>
                  }
                />
              </List.Item>
            )}
          />
        )}
      </Modal>

      {/* Netflix-style Popup for New Messages */}
      <Modal
        open={netflixModalOpen}
        onCancel={handleNetflixModalClose}
        footer={[
          <Button key="close" onClick={handleNetflixModalClose}>
            Dismiss
          </Button>,
          <Button
            key="view"
            type="primary"
            onClick={() => {
              handleNetflixModalClose();
              setIsModalOpen(true);
            }}
          >
            View All Messages
          </Button>
        ]}
        width={400}
        centered
        className="netflix-notification-modal"
      >
        {latestUnreadNotification && (
          <div style={{ textAlign: 'center', padding: '20px 0' }}>
            <MessageOutlined
              style={{
                fontSize: '48px',
                color: '#1890ff',
                marginBottom: '16px',
                display: 'block'
              }}
            />
            <Typography.Title level={4} style={{ marginBottom: '8px' }}>
              New Message
            </Typography.Title>
            <Typography.Title level={5} style={{ marginBottom: '16px', color: '#1890ff' }}>
              {latestUnreadNotification.title}
            </Typography.Title>
            <Paragraph style={{ fontSize: '14px', color: '#666' }}>
              {latestUnreadNotification.message}
            </Paragraph>
          </div>
        )}
      </Modal>

      <style jsx>{`
        .netflix-notification-modal .ant-modal-content {
          background: linear-gradient(135deg, #1a1a1a 0%, #2d1b69 100%);
          border-radius: 16px;
          border: 1px solid rgba(24, 144, 255, 0.3);
        }

        .netflix-notification-modal .ant-modal-header {
          background: transparent;
          border-bottom: none;
        }

        .netflix-notification-modal .ant-modal-body {
          background: transparent;
        }

        .netflix-notification-modal .ant-modal-footer {
          background: transparent;
          border-top: 1px solid rgba(24, 144, 255, 0.2);
        }
      `}</style>
    </>
  );
};

export default AdminNotificationCenter;